import { makeApiRequest } from '@/src/lib/api-request'
import { ErrorMessages, ErrorResponse } from '@/src/types/api-error'
import { useMutation } from '@tanstack/react-query'
import { useQueryClient } from '@tanstack/react-query'
import { AxiosError } from 'axios'

interface VerificationDetails {
  name: string
  pan: string
  accountNo: string
  ifsc: string
  aadharNumber?: string
  dob: string
  otp?: string
  gstNumber?: string

  errorCallback: (
    errorType: 'pan' | 'accountNo' | 'ifsc' | 'root',
    errorMessage: string
  ) => void
}

interface StartKYCResponse {
  respCode: string
  message: string
  data: {
    sessionId: string
    redirectUrl?: string
    status: string
  }
}

export const useStartKYCMutation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationKey: ['user', 'verification-details'],
    mutationFn: async (data: VerificationDetails) => {
      return makeApiRequest({
        url: '/earnings/kyc/start',
        method: 'POST',
        data: data,
        isAuthenticated: true,
        fallbackError: '',
        onError: (e) => {
          if (e instanceof AxiosError) {
            const error = e.response?.data as ErrorResponse
            if (error?.message && error?.message in ErrorMessages) {
              const panErrors = [
                'error/invalid-pan',
                'error/pan-dupl',
                'error/pan-name-mismatch',
                'error/pan-verify-fail',
              ]
              const bankErrors = [
                'error/invalid-acc-num',
                'error/bank-acc-dupl',
                'error/bank-verify-fail',
              ]
              const ifscErrors = ['error/invalid-ifsc']
              const verificationErrors = ['error/verification-window-closed']
              if (verificationErrors.includes(error.message)) {
                data.errorCallback('root', ErrorMessages[error.message])
              } else if (panErrors.includes(error.message)) {
                data.errorCallback('pan', ErrorMessages[error.message])
              } else if (bankErrors.includes(error.message)) {
                data.errorCallback('accountNo', ErrorMessages[error.message])
              } else if (ifscErrors.includes(error.message)) {
                data.errorCallback('ifsc', ErrorMessages[error.message])
              } else {
                data.errorCallback('root', ErrorMessages[error.message])
              }
            } else {
              data.errorCallback(
                'root',
                error?.message || 'Failed to verify your details'
              )
            }
          } else if (e instanceof Error) {
            data.errorCallback(
              'root',
              e.message || 'Failed to verify your details'
            )
          }
        },
        isV3Api: true,
      }) as Promise<StartKYCResponse>
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['wallet-balance'] })
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['verification-status'] })
    },
  })
}
